import type { ChargeItem, Extension } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

/** The Practitioner and Biller notes a charge carries, as the billing surfaces show them. */
export type ChargeItemNotes = {
  /** The Billing Note, written by a biller. */
  billerNotes?: string;
  /** The Procedure Note, written by the provider who charted the procedure. */
  providerNotes?: string;
};

const NOTE_EXTENSION_URLS = [
  Extensions.ChargeItemBillerNotes,
  Extensions.ChargeItemProviderNotes,
] as const;

/**
 * Reads one note off a charge.
 *
 * Same shape as the Services tab reads a custom service's note off its Procedure: find the
 * extension by url, trim it, and treat a blank one as no note at all.
 *
 * @param chargeItem The charge to read from.
 * @param url Which note to read — {@link Extensions.ChargeItemBillerNotes} or
 *   {@link Extensions.ChargeItemProviderNotes}.
 * @returns The trimmed note, or an empty string when the charge carries none.
 */
export function resolveChargeItemNote(chargeItem: ChargeItem, url: Extensions): string {
  return (
    chargeItem.extension?.find((extension) => extension.url === url)?.valueString?.trim() ?? ""
  );
}

/**
 * One extension per non-blank note, so a charge with neither carries no note extension.
 *
 * @param notes The notes to stamp. Blank and whitespace-only values are dropped.
 * @returns The extensions to put on the charge, in Biller then Provider order.
 */
export function buildChargeItemNoteExtensions({
  billerNotes,
  providerNotes,
}: ChargeItemNotes): Extension[] {
  const values: [Extensions, string | undefined][] = [
    [Extensions.ChargeItemBillerNotes, billerNotes],
    [Extensions.ChargeItemProviderNotes, providerNotes],
  ];

  return values.reduce<Extension[]>((extensions, [url, value]) => {
    const valueString = value?.trim();
    if (valueString) extensions.push({ url, valueString });
    return extensions;
  }, []);
}

/**
 * Replaces a charge's note extensions with the given notes.
 *
 * Every other extension is kept as-is, and both note urls are dropped before the new ones are
 * added, so a charge never carries more than one of each and a note cleared to blank stays cleared.
 *
 * @param extensions The charge's current extensions.
 * @param notes The notes the write ended with.
 * @returns The extension list to write back.
 */
export function replaceChargeItemNoteExtensions(
  extensions: Extension[] | undefined,
  notes: ChargeItemNotes,
): Extension[] {
  const kept = (extensions ?? []).filter(
    ({ url }) => !NOTE_EXTENSION_URLS.some((noteUrl) => noteUrl === url),
  );

  return [...kept, ...buildChargeItemNoteExtensions(notes)];
}
